import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  SERVICE_MAIN_CALL_MESSAGE,
  SERVICE_QUICK_BUTTONS,
} from '../constants/serviceQuickButtons';
import {
  ackRoomService,
  callRoomService,
  fetchRoomService,
} from '../services/partyScreenApi';
import LocalVoiceWakeAssistant from './LocalVoiceWakeAssistant';

function ServiceFocusIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M4 8V4h4M20 8V4h-4M4 16v4h4M20 16v4h-4M9 12h6"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

const ACTIVE_MAIN_STATUSES = ['pending', 'serving', 'hold'];

function isMainCallRequest(req) {
  return String(req?.message || '').trim() === SERVICE_MAIN_CALL_MESSAGE;
}

/** 主按钮仅跟踪「呼叫服务」这一条，不受快捷按钮影响 */
function deriveMainCallPhase(requests) {
  const list = Array.isArray(requests) ? requests : [];
  const primary = list.find(
    (r) => ACTIVE_MAIN_STATUSES.includes(r.status) && isMainCallRequest(r)
  );
  if (primary) return { phase: 'calling', primary };
  return { phase: 'idle', primary: null };
}

export default function ServiceCallPanel({
  roomId,
  variant = 'embedded',
  showPageLink = false,
}) {
  const isFocus = variant === 'focus';
  const [enabled, setEnabled] = useState(true);
  const [staff, setStaff] = useState([]);
  const [requests, setRequests] = useState([]);
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [mainCallLoading, setMainCallLoading] = useState(false);
  const [optimisticCalling, setOptimisticCalling] = useState(false);
  const [feedback, setFeedback] = useState('');
  const [expanded, setExpanded] = useState(true);
  const messageInputId = isFocus ? 'service-message-focus' : 'service-message';

  const { phase: serverPhase, primary } = useMemo(() => deriveMainCallPhase(requests), [requests]);
  const phase = optimisticCalling && serverPhase === 'idle' ? 'calling' : serverPhase;

  useEffect(() => {
    if (serverPhase === 'calling') setOptimisticCalling(false);
  }, [serverPhase]);

  const reloadService = useCallback(async () => {
    const data = await fetchRoomService(roomId);
    setEnabled(data?.enabled !== false);
    setStaff(Array.isArray(data?.staff) ? data.staff : []);
    setRequests(Array.isArray(data?.requests) ? data.requests : []);
    return data;
  }, [roomId]);

  useEffect(() => {
    if (!roomId) return undefined;
    let cancelled = false;
    const load = async () => {
      try {
        if (!cancelled) await reloadService();
      } catch {
        if (!cancelled) {
          setStaff([]);
          setRequests([]);
        }
      }
    };
    load();
    const interval = phase === 'calling' ? 3000 : 5000;
    const timer = setInterval(load, interval);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [roomId, reloadService, phase]);

  const submitCall = async (text, { clearMessage = false } = {}) => {
    const trimmed = String(text || '').trim();
    if (!trimmed) {
      setFeedback('请填写服务要求');
      return;
    }
    setSubmitting(true);
    setFeedback('');
    try {
      await callRoomService(roomId, trimmed);
      if (clearMessage) setMessage('');
      setFeedback(trimmed === SERVICE_MAIN_CALL_MESSAGE ? '已呼叫，服务员将尽快前来' : '已提交');
      setExpanded(true);
      await reloadService();
    } catch (e) {
      setFeedback(e?.message || '提交失败，请重试');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = () => submitCall(message, { clearMessage: true });

  const handleQuickCall = (label) => {
    if (!enabled || submitting || mainCallLoading) return;
    submitCall(label);
  };

  const handleMainCall = async () => {
    if (!enabled || submitting || mainCallLoading) return;
    setMainCallLoading(true);
    setFeedback('');
    try {
      if (phase === 'idle') {
        setOptimisticCalling(true);
        await callRoomService(roomId, SERVICE_MAIN_CALL_MESSAGE);
        setFeedback('正在呼叫服务…');
        await reloadService();
      } else if (phase === 'calling' && primary?.id) {
        await ackRoomService(roomId, primary.id);
        setOptimisticCalling(false);
        setFeedback('已恢复，可再次呼叫服务');
        await reloadService();
      } else if (phase === 'calling') {
        setOptimisticCalling(false);
        await reloadService();
      }
    } catch (e) {
      setOptimisticCalling(false);
      setFeedback(e?.message || '操作失败，请重试');
    } finally {
      setMainCallLoading(false);
    }
  };

  const statusLabel = (status) => {
    if (status === 'pending') return '待响应';
    if (status === 'serving') return '服务中';
    if (status === 'hold') return '已挂起';
    if (status === 'done') return '已完成';
    return status;
  };

  const mainCallLabel = (() => {
    if (mainCallLoading && phase === 'idle') return '提交中…';
    if (mainCallLoading && phase === 'calling') return '处理中…';
    if (phase === 'calling') return '呼叫服务中';
    return '呼叫服务';
  })();

  const mainCallHint = (() => {
    if (phase !== 'calling' || !primary) {
      if (phase === 'calling') return '点击恢复呼叫服务';
      return null;
    }
    if (primary.status === 'pending') {
      return primary.assigned_staff_name
        ? `已分配 ${primary.assigned_staff_name} · 点击恢复呼叫服务`
        : '等待服务员响应 · 点击恢复呼叫服务';
    }
    if (primary.assigned_staff_name) {
      return `${primary.assigned_staff_name} 服务中 · 点击恢复呼叫服务`;
    }
    return '点击恢复呼叫服务';
  })();

  const staffPanel = (
    <div className="service-col">
      <div className="service-block-label">在岗服务员</div>
      <div className="service-scroll-area">
        {staff.length ? (
          <ul className="service-staff-list">
            {staff.map((person) => (
              <li
                key={person.id}
                className={
                  person.work_status === 'serving'
                    ? 'is-serving'
                    : person.work_status === 'busy'
                      ? 'is-busy'
                      : ''
                }
              >
                <span
                  className={`service-staff-dot${
                    person.work_status === 'serving'
                      ? ' service-staff-dot--busy'
                      : person.work_status === 'busy'
                        ? ' service-staff-dot--warn'
                        : ''
                  }`}
                />
                <div className="service-staff-info">
                  <span className="service-staff-name">{person.name}</span>
                  <span className="service-staff-meta">
                    {person.work_status_label || (person.serving ? '服务中' : '空闲')}
                    {person.active_count > 1 ? ` · ${person.active_count}单` : ''}
                    {person.source === 'schedule' && person.shift ? ` · ${person.shift}` : ''}
                  </span>
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <div className="service-empty">暂无在岗服务员</div>
        )}
      </div>
    </div>
  );

  const requestsPanel = (
    <div className="service-col">
      <div className="service-block-label">本房服务请求</div>
      <div className="service-scroll-area">
        {requests.length ? (
          <ul className="service-request-list">
            {requests.map((req) => (
              <li key={req.id}>
                <span className={`service-request-status service-request-status--${req.status}`}>
                  {statusLabel(req.status)}
                </span>
                <span className="service-request-message">{req.message}</span>
                {req.assigned_staff_name ? (
                  <span className="service-request-staff">{req.assigned_staff_name}</span>
                ) : null}
              </li>
            ))}
          </ul>
        ) : (
          <div className="service-empty">暂无服务请求</div>
        )}
      </div>
    </div>
  );

  const mainCallButton = isFocus ? (
    <div className="service-center-call">
      <button
        type="button"
        className={`service-main-call-btn service-main-call-btn--${phase === 'calling' ? 'calling' : 'idle'}${mainCallLoading ? ' is-loading' : ''}`}
        onClick={handleMainCall}
        disabled={!enabled || submitting || mainCallLoading}
        aria-live="polite"
      >
        {phase === 'calling' ? (
          <>
            <span className="service-main-call-pulse" aria-hidden="true" />
            <span className="service-main-call-text">{mainCallLabel}</span>
          </>
        ) : (
          <span className="service-main-call-text">{mainCallLabel}</span>
        )}
      </button>
      {mainCallHint ? <p className="service-main-call-hint">{mainCallHint}</p> : null}
    </div>
  ) : null;

  return (
    <section className={`service-panel${isFocus ? ' service-panel--focus' : ''}`}>
      <div className="panel-head service-panel-head">
        <div className="service-panel-title-wrap">
          <div className="service-panel-title-row">
            <h2 className="panel-title">呼叫服务</h2>
            {showPageLink && roomId ? (
              <Link
                to={`/room/${roomId}/service`}
                className="service-focus-link"
                title="打开服务专页"
                aria-label="打开服务专页"
              >
                <ServiceFocusIcon />
              </Link>
            ) : null}
          </div>
          <p className="panel-subtitle">
            {isFocus ? '本房间服务呼叫与在岗状态' : '在岗状态与服务请求'}
          </p>
        </div>
        {enabled && !isFocus ? (
          <button
            type="button"
            className="service-call-btn"
            onClick={() => setExpanded((v) => !v)}
          >
            {expanded ? '收起' : '呼叫服务'}
          </button>
        ) : null}
      </div>

      {!enabled ? (
        <div className="service-off">呼叫服务已关闭</div>
      ) : (
        <>
          <div className={isFocus ? 'service-focus-grid' : 'service-split'}>
            {staffPanel}
            {mainCallButton}
            {requestsPanel}
          </div>

          {isFocus ? (
            <div className="service-quick-bar">
              <div className="service-block-label">快捷呼叫</div>
              <div className="service-quick-buttons">
                {SERVICE_QUICK_BUTTONS.map((label) => (
                  <button
                    key={label}
                    type="button"
                    className="service-quick-btn"
                    disabled={submitting || mainCallLoading}
                    onClick={() => handleQuickCall(label)}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
          ) : null}

          <LocalVoiceWakeAssistant compact={!isFocus} />

          {expanded || isFocus ? (
            <div className="service-form-block">
              <label className="service-block-label" htmlFor={messageInputId}>
                服务要求
              </label>
              <textarea
                id={messageInputId}
                className="service-message-input"
                placeholder="例如：需要加热水、收拾桌面、帮忙拍照…"
                value={message}
                maxLength={200}
                rows={isFocus ? 3 : 2}
                onChange={(e) => setMessage(e.target.value)}
              />
              <div className="service-form-footer">
                <span className="service-char-count">{message.length}/200</span>
                <button
                  type="button"
                  className="service-submit-btn"
                  disabled={submitting || mainCallLoading || !message.trim()}
                  onClick={handleSubmit}
                >
                  {submitting ? '提交中…' : '提交呼叫'}
                </button>
              </div>
              {feedback ? <p className="service-feedback">{feedback}</p> : null}
            </div>
          ) : null}
        </>
      )}
    </section>
  );
}
